import { useState } from "react";
import { downloadLogs, flattenLogPages, useLogsInfinite } from "../api/hooks/useLogs";
import { useBotInstances } from "../api/hooks/useBotInstances";
import { LogRow } from "../components/LogRow/LogRow";
import type { LogLevelFilter } from "../api/types";

const LEVELS: { key: LogLevelFilter; label: string }[] = [
  { key: "all", label: "Все" },
  { key: "error", label: "Ошибки" },
  { key: "warning", label: "Предупр." },
  { key: "info", label: "Инфо" },
];

export function Logs() {
  const [level, setLevel] = useState<LogLevelFilter>("all");
  const [query, setQuery] = useState("");
  const [collapse, setCollapse] = useState(true);
  const [botFilter, setBotFilter] = useState<string>("all");
  const { data: botInstances } = useBotInstances();

  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useLogsInfinite({
    level,
    q: query,
    collapse_repeats: collapse,
    bot_instance_id: botFilter === "all" ? undefined : botFilter,
  });
  const rows = flattenLogPages(data?.pages);
  const counts = data?.pages[0]?.counts;
  const topError = data?.pages[0]?.top_error;

  return (
    <div style={{ padding: "26px 28px 44px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <div style={{ display: "flex", gap: 2, padding: 2, borderRadius: 11, background: "var(--surface2)" }}>
          {LEVELS.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => setLevel(l.key)}
              style={{
                padding: "6px 13px",
                border: 0,
                borderRadius: 9,
                cursor: "pointer",
                font: "600 12px 'Golos Text',sans-serif",
                background: level === l.key ? "var(--accent)" : "transparent",
                color: level === l.key ? "var(--onfill)" : "var(--fg2)",
              }}
            >
              {l.label} {counts?.[l.key] ?? 0}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Фильтр по тексту"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            flex: "1 1 220px",
            maxWidth: 340,
            padding: "7px 12px",
            border: "1px solid var(--line)",
            borderRadius: 10,
            background: "var(--surface)",
            color: "var(--fg)",
            font: "400 12.5px 'JetBrains Mono',monospace",
            outline: "none",
          }}
        />
        {botInstances && botInstances.instances.length > 1 && (
          <select
            value={botFilter}
            onChange={(e) => setBotFilter(e.target.value)}
            title="Фильтр по боту"
            style={{
              padding: "6px 11px",
              border: "1px solid var(--line)",
              borderRadius: 10,
              cursor: "pointer",
              font: "600 12px 'JetBrains Mono',monospace",
              background: "var(--surface2)",
              color: "var(--fg2)",
            }}
          >
            <option value="all">Все боты</option>
            {botInstances.instances.map((b) => (
              <option key={b.bot_instance_id} value={b.bot_instance_id}>
                {b.display_name ?? b.bot_instance_id}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => setCollapse((c) => !c)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "7px 12px",
            border: "1px solid var(--line2)",
            borderRadius: 9,
            background: "transparent",
            color: "var(--fg2)",
            cursor: "pointer",
            font: "500 12px 'Golos Text',sans-serif",
          }}
        >
          <span
            style={{
              width: 26,
              height: 15,
              borderRadius: 9,
              flex: "none",
              padding: 2,
              display: "flex",
              justifyContent: collapse ? "flex-end" : "flex-start",
              background: collapse ? "var(--accent)" : "var(--line2)",
            }}
          >
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: "var(--onfill)" }} />
          </span>
          Свернуть повторы
        </button>
        <div style={{ flex: 1, minWidth: 4 }} />
        <button
          type="button"
          onClick={() => downloadLogs({ level, q: query, collapse_repeats: collapse, bot_instance_id: botFilter === "all" ? undefined : botFilter })}
          style={{
            padding: "7px 14px",
            border: "1px solid var(--line2)",
            borderRadius: 9,
            background: "transparent",
            color: "var(--fg2)",
            cursor: "pointer",
            font: "600 12px 'Golos Text',sans-serif",
          }}
        >
          Скачать полный лог
        </button>
      </div>

      {topError && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "11px 15px", borderRadius: 11, background: "var(--shortsoft)", border: "1px solid var(--line)" }}>
          <span style={{ font: "600 12.5px 'Golos Text',sans-serif", color: "var(--short)" }}>Повторяющийся сбой</span>
          <span style={{ font: "400 12.5px 'Golos Text',sans-serif", color: "var(--fg2)" }}>
            {topError.count} одинаковых ошибок — {topError.message}
          </span>
        </div>
      )}

      <section style={{ borderRadius: 16, background: "var(--surface)", border: "1px solid var(--line)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
        {rows.map((l) => (
          <LogRow key={l.id} log={l} />
        ))}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: 12 }}>
          <span style={{ font: "400 11.5px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>
            {isFetching && !isFetchingNextPage ? "загрузка…" : `показано ${rows.length}`}
          </span>
          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              style={{
                padding: "6px 14px",
                border: "1px solid var(--line2)",
                borderRadius: 8,
                background: "transparent",
                color: "var(--fg2)",
                cursor: isFetchingNextPage ? "default" : "pointer",
                font: "600 12px 'Golos Text',sans-serif",
                opacity: isFetchingNextPage ? 0.6 : 1,
              }}
            >
              {isFetchingNextPage ? "Загрузка…" : "Загрузить ещё"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
